import { useState } from 'react';

import type { OrderStatusFilter } from '@solar/api-client';
import { ErrorBox, Spinner, formatDate, formatPhone, ORDER_STATE_LABEL } from '@solar/ui';
import { useAsync } from '@solar/ui';

import { api } from '../api';

/** iOS MyListView 필터 4종 파리티 (전체/설치완료/미설치/미결제 — Constants.swift INSTALL_TYPE). */
const FILTERS: Array<{ value: OrderStatusFilter; label: string }> = [
  { value: 'incomplete', label: '미완료' },
  { value: 'completed', label: '완료' },
  { value: 'unpaid', label: '미수납' },
  { value: 'all', label: '전체' },
];

export function OrderList({ onOpen }: { onOpen: (id: number) => void }) {
  const [status, setStatus] = useState<OrderStatusFilter>('incomplete');
  const [keyword, setKeyword] = useState('');
  const [applied, setApplied] = useState('');
  // iOS 는 정렬 버튼이 받아온 배열을 그대로 뒤집었다(MyListView.swift:86).
  // 서버 OrderListQuery 에 정렬 파라미터가 없으므로 같은 방식으로 재현한다.
  const [reversed, setReversed] = useState(false);

  // 기사 앱은 항상 본인 배정 건만 본다 (assigned_to_me).
  const orders = useAsync(
    () => api.listOrders({ status, keyword: applied, assigned_to_me: true, size: 50 }),
    [status, applied],
  );

  const items = orders.data
    ? reversed
      ? [...orders.data.items].reverse()
      : orders.data.items
    : [];

  return (
    <>
      <div className="toolbar">
        {FILTERS.map((f) => (
          <button
            key={f.value}
            type="button"
            className={status === f.value ? 'chip active' : 'chip'}
            onClick={() => setStatus(f.value)}
          >
            {f.label}
          </button>
        ))}
        <button
          type="button"
          className={reversed ? 'chip active sort' : 'chip sort'}
          onClick={() => setReversed((r) => !r)}
        >
          {reversed ? '역순 ↑' : '등록순 ↓'}
        </button>
      </div>

      <form
        className="search"
        onSubmit={(e) => {
          e.preventDefault();
          setApplied(keyword.trim());
        }}
      >
        <input
          value={keyword}
          onChange={(e) => setKeyword(e.target.value)}
          placeholder="이름 · 연락처 · 주소"
          inputMode="search"
        />
        <button type="submit">검색</button>
      </form>

      {orders.loading && <Spinner />}
      {orders.error && <ErrorBox message={orders.error} onRetry={orders.reload} />}

      {orders.data && orders.data.items.length === 0 && (
        <p className="empty">배정된 건이 없습니다.</p>
      )}

      <ul className="cards">
        {items.map((order) => (
          <li key={order.id}>
            <button type="button" className="card" onClick={() => onOpen(order.id)}>
              <div className="card__top">
                <strong>{order.user_name ?? '(이름 없음)'}</strong>
                <span className={`badge s${order.state_type}`}>
                  {ORDER_STATE_LABEL[order.state_type] ?? order.state_type}
                </span>
              </div>
              <p className="card__addr">
                {order.addr_full ?? '-'} {order.building_name} {order.apart_number}
              </p>
              <p className="card__meta">
                {formatDate(order.install_date)} · {formatPhone(order.user_phone)}
                {order.temp_save_yn === 'Y' && <span className="draft"> 임시저장</span>}
                {/* iOS OrderListCell 의 결제완료/미결제 표시 파리티 */}
                {order.payment_check !== 'Y' && <span className="unpaid"> 미수납</span>}
              </p>
            </button>
          </li>
        ))}
      </ul>

      {orders.data && orders.data.total > orders.data.items.length && (
        <p className="muted">
          {orders.data.items.length} / {orders.data.total}건 표시 — 검색으로 범위를 좁혀주세요.
        </p>
      )}
    </>
  );
}
